import { useEffect, useState } from "react";
import { Loader2, RefreshCcw, Activity, AlertCircle, CheckCircle2, Circle, Clock } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

type CronJob = {
  jobid: number;
  jobname: string;
  schedule: string;
  active: boolean;
  command: string;
  last_start_time: string | null;
  last_end_time: string | null;
  last_status: string | null;
  last_return_message: string | null;
};

type CronRun = {
  runid: number;
  jobid: number;
  start_time: string;
  end_time: string | null;
  status: string;
  return_message: string | null;
};

const formatTime = (iso: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
};

const StatusPill = ({ status }: { status: string | null }) => {
  if (!status) {
    return (
      <Badge variant="secondary" className="gap-1">
        <Circle className="h-3 w-3" /> never run
      </Badge>
    );
  }
  const s = status.toLowerCase();
  if (s === "succeeded") {
    return (
      <Badge className="gap-1 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300">
        <CheckCircle2 className="h-3 w-3" /> succeeded
      </Badge>
    );
  }
  if (s === "failed") {
    return (
      <Badge variant="destructive" className="gap-1">
        <AlertCircle className="h-3 w-3" /> failed
      </Badge>
    );
  }
  if (s === "running" || s === "starting") {
    return (
      <Badge variant="secondary" className="gap-1">
        <Clock className="h-3 w-3" /> {s}
      </Badge>
    );
  }
  return <Badge variant="outline">{s}</Badge>;
};

export function JobsTab() {
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CronJob | null>(null);
  const [runs, setRuns] = useState<CronRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase.functions.invoke("admin-cron-health");
    if (err || (data && (data as { error?: string }).error)) {
      setError(err?.message || (data as { error?: string }).error || "Failed to load");
    } else {
      setJobs((data as { jobs: CronJob[] }).jobs ?? []);
    }
    setLoading(false);
  };

  const loadRuns = async (job: CronJob) => {
    setSelected(job);
    setRunsLoading(true);
    setRuns([]);
    const { data, error: err } = await supabase.functions.invoke(
      `admin-cron-health?jobid=${job.jobid}`,
      { method: "GET" },
    );
    if (err) {
      toast.error(err.message);
    } else {
      setRuns((data as { runs: CronRun[] }).runs ?? []);
    }
    setRunsLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="space-y-4">
      <Card className="shadow-soft">
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" /> Scheduled jobs
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-3 inline-flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-1.5 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No scheduled jobs found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-3">Job</th>
                    <th className="py-2 pr-3">Schedule</th>
                    <th className="py-2 pr-3">Active</th>
                    <th className="py-2 pr-3">Last run</th>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr
                      key={job.jobid}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/50"
                      onClick={() => void loadRuns(job)}
                    >
                      <td className="py-2 pr-3 font-medium">{job.jobname || `job ${job.jobid}`}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{job.schedule}</td>
                      <td className="py-2 pr-3">
                        {job.active ? (
                          <Badge variant="outline">on</Badge>
                        ) : (
                          <Badge variant="secondary">paused</Badge>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-xs text-muted-foreground">{formatTime(job.last_start_time)}</td>
                      <td className="py-2 pr-3">
                        <StatusPill status={job.last_status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>{selected?.jobname || `Job ${selected?.jobid}`}</SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="mt-4 space-y-4">
              <div className="rounded-md bg-muted/50 p-3 text-xs">
                <div className="font-mono">Schedule: {selected.schedule}</div>
                <div className="mt-1 break-all font-mono text-muted-foreground">
                  {selected.command}
                </div>
              </div>
              <div>
                <h4 className="mb-2 text-sm font-medium">Last 20 runs</h4>
                {runsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : runs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No runs recorded.</p>
                ) : (
                  <ul className="space-y-2">
                    {runs.map((r) => (
                      <li key={r.runid} className="rounded-md border p-2 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono">{formatTime(r.start_time)}</span>
                          <StatusPill status={r.status} />
                        </div>
                        {r.return_message && (
                          <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-words text-muted-foreground">
                            {r.return_message}
                          </pre>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
